import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SideBar from '../../components/Topbar';
import { HTMLCourseNavProvider } from "../../components/SidebarCourseContext";
import './assets/styles/HTMLLesson1.css';
import { CopyBlock, dracula } from "react-code-blocks";

// Import sophisticated icons
import { 
  FaCode,
  FaCheck,
  FaArrowRight,
  FaPlay,
  FaBookOpen,
  FaFingerprint,
  FaCss3,
  FaJs,
  FaHashtag,
  FaAnchor
} from 'react-icons/fa';

const HTMLLesson9 = () => {
  const [activeSection, setActiveSection] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [viewedSections, setViewedSections] = useState(new Set([0]));
  const sectionRefs = useRef([]);
  const observerRef = useRef(null);

  const [language] = useState("html");
  const [lineNumbers] = useState(true);

  // Video explanation URL
  const videoUrl = "https://app.codeflarelimited.com/watch-video/html-course";

  const code1 = `<style>
#myHeader {
  background-color: lightblue;
  color: black;
  padding: 40px;
  text-align: center;
}
</style>
<h1 id="myHeader">My header</h1>`;

  const code2 = `<!-- HTML -->
<nav id="mainNavigation">
  <ul>
    <li><a href="#home">Home</a></li>
    <li><a href="#about">About</a></li>
  </ul>
</nav>

<section id="home">
  <h2>Welcome Home</h2>
</section>

<section id="about">
  <h2>About Us</h2>
</section>`;

  const code3 = `/* CSS */
#mainNavigation {
  background: #333;
  padding: 1rem;
}

#home {
  background: #f0f8ff;
  padding: 2rem;
}

#about {
  background: #fff0f5;
  padding: 2rem;
}`;

  const code4 = `// JavaScript
// Scroll to element
document.getElementById('home').scrollIntoView();

// Change content
document.getElementById('mainTitle').innerHTML = 'New Title';

// Add event listener
document.getElementById('myButton').addEventListener('click', function() {
  alert('Button clicked!');
});`;

  const code5 = `<!-- Page Navigation -->
<a href="#section1">Go to Section 1</a>
<a href="#contact-form">Contact Us</a>

<!-- Target elements -->
<section id="section1">
  <h2>Section 1 Content</h2>
</section>

<form id="contact-form">
  <input type="email" placeholder="Your email">
  <button type="submit">Send</button>
</form>`;

  const code6 = `<!DOCTYPE html>
<html>
<head>
  <style>
    /* ID Selector Example */
    #page-header {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 3rem;
      text-align: center;
      border-radius: 10px;
      margin-bottom: 2rem;
    }
    
    #main-content {
      background: #f8f9fa;
      padding: 2rem;
      border-radius: 8px;
      border-left: 4px solid #667eea;
    }
    
    #featured-article {
      background: white;
      padding: 1.5rem;
      border-radius: 8px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    }
    
    #call-to-action {
      background: #28a745;
      color: white;
      padding: 1rem 2rem;
      border: none;
      border-radius: 5px;
      cursor: pointer;
      font-size: 1.1rem;
    }
    
    #call-to-action:hover {
      background: #218838;
    }
    
    #page-footer {
      background: #343a40;
      color: white;
      text-align: center;
      padding: 2rem;
      margin-top: 3rem;
    }
  </style>
</head>
<body>
  <!-- Header with ID -->
  <header id="page-header">
    <h1>Welcome to Our Website</h1>
    <p>Learn HTML ID attributes like a pro</p>
  </header>
  
  <!-- Main content area -->
  <main id="main-content">
    <article id="featured-article">
      <h2>Featured Content</h2>
      <p>This article demonstrates proper use of ID attributes.</p>
      <button id="call-to-action">Learn More</button>
    </article>
  </main>
  
  <!-- Footer -->
  <footer id="page-footer">
    <p>&copy; 2024 Web Development Guide</p>
  </footer>
  
  <script>
    // JavaScript using IDs
    document.getElementById('call-to-action').addEventListener('click', function() {
      alert('Thanks for your interest!');
    });
  </script>
</body>
</html>`;

  const sections = [
    {
      title: "Understanding ID Selectors",
      content: "The id attribute specifies a unique identifier for an HTML element that must be unique within the entire document",
      keyPoints: [
        "Each id value must be unique in the HTML document",
        "Used by CSS and JavaScript to target specific elements",
        "Syntax: id='unique-name' (no spaces allowed)",
        "Case-sensitive and should be descriptive"
      ],
      icon: <FaFingerprint className="section-icon" />,
      color: "#2563eb",
      code: null
    },
    {
      title: "ID Selectors in CSS", 
      content: "In CSS, select an element with a specific id by using a hash (#) character followed by the id name",
      keyPoints: [
        "CSS syntax: #id-name { styles }",
        "Has higher specificity than class and tag selectors",
        "Use for unique, one-off styling",
        "Perfect for main layout components"
      ],
      icon: <FaCss3 className="section-icon" />,
      color: "#1d4ed8",
      code: code1
    },
    {
      title: "ID Selectors in JavaScript",
      content: "JavaScript can access and manipulate elements using their id through document.getElementById()",
      keyPoints: [
        "Access elements: document.getElementById('id-name')",
        "Manipulate content, styles, and behavior",
        "Add event listeners to specific elements",
        "Essential for interactive web pages"
      ],
      icon: <FaJs className="section-icon" />,
      color: "#1e40af",
      code: code4
    },
    {
      title: "Page Navigation with IDs",
      content: "IDs enable smooth page navigation through anchor links that jump to specific sections",
      keyPoints: [
        "Create links: <a href='#section-id'>Go to Section</a>",
        "Target elements: <section id='section-id'>",
        "Useful for table of contents and navigation menus",
        "Works without JavaScript for basic page jumping"
      ],
      icon: <FaAnchor className="section-icon" />,
      color: "#7c3aed",
      code: code5
    }
  ];

  // Intersection Observer for scroll detection
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '-10% 0px -20% 0px',
      threshold: 0.6
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const index = sectionRefs.current.indexOf(entry.target);
        
        if (entry.isIntersecting) {
          setActiveSection(index);
          setViewedSections(prev => new Set([...prev, index]));
        }
      });
    }, options);

    sectionRefs.current.forEach((section) => {
      if (section) {
        observerRef.current.observe(section);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const addSectionRef = (element, index) => {
    sectionRefs.current[index] = element;
  };

  const getCompletionPercentage = () => {
    const totalSections = sections.length + 3;
    const viewedCount = viewedSections.size;
    return Math.round((viewedCount / totalSections) * 100);
  };

  return (
    <HTMLCourseNavProvider>
      <div className="html-lesson-container">
        <SideBar />
        
        <div className="html-lesson-main">
          {/* Header Section */}
          <div className="html-lesson-header">
            <div className="html-header-content">
              <div className="html-lesson-badge">
                <FaBookOpen className="html-badge-icon" />
                Lesson 9 • {getCompletionPercentage()}% Viewed
              </div>
              <h1 className="html-lesson-title">HTML ID Selectors</h1>
              <p className="html-lesson-subtitle">
                Master unique element identification for CSS styling, JavaScript manipulation, and page navigation
              </p>
              
              {/* Progress Bar */}
              <div className="html-completion-progress">
                <div 
                  className="html-completion-fill"
                  style={{ width: `${getCompletionPercentage()}%` }}
                ></div>
                <div className="html-progress-labels">
                  <span>Getting Started</span>
                  <span>Complete</span>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="html-progress-indicator">
            {sections.map((section, index) => (
              <div 
                key={index}
                className={`html-progress-step ${viewedSections.has(index) ? 'html-viewed' : ''} ${index === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-step-number">
                  {viewedSections.has(index) ? '✓' : index + 1}
                </div>
                <div className="html-step-label">{section.title.split(' ')[0]}</div>
                {index < sections.length - 1 && <div className="html-step-connector"></div>}
              </div>
            ))}
          </div>

          {/* Visual ID Demonstration */}
          <div 
            className="html-visual-id"
            ref={(el) => addSectionRef(el, 0)}
          >
            <div className="html-visual-header">
              <div className="html-code-badge">
                <FaHashtag className="html-badge-icon" />
                ID Selector Syntax
              </div>
              <h3 className="html-visual-title">
                How ID Selectors Work
              </h3>
            </div>
            
            <div className="html-id-syntax">
              <div className="html-syntax-item">
                <div className="html-syntax-label">HTML</div>
                <code>&lt;div <span className="html-attribute">id=</span><span className="html-value">"unique-name"</span>&gt;</code>
              </div>
              
              <div className="html-syntax-arrow">→</div>
              
              <div className="html-syntax-item">
                <div className="html-syntax-label">CSS</div>
                <code><span className="html-selector">#unique-name</span> {`{ styles }`}</code>
              </div>
              
              <div className="html-syntax-arrow">→</div>
              
              <div className="html-syntax-item">
                <div className="html-syntax-label">JavaScript</div>
                <code>document.<span className="html-method">getElementById</span>(<span className="html-value">'unique-name'</span>)</code>
              </div>
            </div>
          </div>

          {/* Content Sections */}
          <div className="html-content-sections">
            {sections.map((section, index) => (
              <div 
                key={index}
                ref={(el) => addSectionRef(el, index + 1)}
                className={`html-content-card ${viewedSections.has(index + 1) ? 'html-viewed' : ''} ${(index + 1) === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-card-badge">
                  {viewedSections.has(index + 1) ? '✓ Viewed' : `Part ${index + 1}`}
                </div>
                
                <div className="html-card-header">
                  <div className="html-card-icon" style={{ backgroundColor: section.color }}>
                    {section.icon}
                  </div>
                  <div className="html-card-text">
                    <h2 className="html-card-title">{section.title}</h2>
                    <p className="html-card-content">{section.content}</p>
                    
                    <div className="html-key-points">
                      <h4>Key Points:</h4>
                      <ul>
                        {section.keyPoints.map((point, idx) => (
                          <li key={idx}>{point}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Code Example for sections that have code */}
                {section.code && (
                  <div className="html-section-code">
                    <div className="html-code-badge">
                      <FaCode className="html-badge-icon" />
                      Example
                    </div>
                    <div className="html-code-block-small">
                      <CopyBlock
                        language={language}
                        text={section.code}
                        showLineNumbers={lineNumbers}
                        theme={dracula}
                        wrapLines={true}
                        codeBlock
                      />
                    </div>
                  </div>
                )}
                
                <div className="html-card-actions">
                  <a 
                    href={videoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="html-action-btn"
                  >
                    <FaPlay className="html-btn-icon" />
                    Watch Video Explanation
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* ID vs Class Comparison */}
          <div 
            className="html-comparison-section"
            ref={(el) => addSectionRef(el, sections.length + 1)}
          >
            <div className="html-comparison-header">
              <div className="html-code-badge">
                <FaFingerprint className="html-badge-icon" />
                ID vs Class
              </div>
              <h3 className="html-comparison-title">
                When to Use ID vs Class
              </h3>
            </div>
            
            <div className="html-comparison-grid">
              <div className="html-comparison-card">
                <div className="html-comparison-icon" style={{ backgroundColor: '#3b82f6' }}>
                  <FaHashtag />
                </div>
                <h4>ID Selectors</h4>
                <div className="html-comparison-list">
                  <div className="html-comparison-item html-do">
                    <strong>Use For:</strong>
                    <ul>
                      <li>Unique page elements (header, footer)</li>
                      <li>JavaScript manipulation</li>
                      <li>Page anchor links</li>
                      <li>Main layout sections</li>
                    </ul>
                  </div>
                  <div className="html-comparison-item html-dont">
                    <strong>Avoid For:</strong>
                    <ul>
                      <li>Reusable styling</li>
                      <li>Multiple elements</li>
                      <li>General styling</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="html-comparison-card">
                <div className="html-comparison-icon" style={{ backgroundColor: '#8b5cf6' }}>
                  <FaCss3 />
                </div>
                <h4>Class Selectors</h4>
                <div className="html-comparison-list">
                  <div className="html-comparison-item html-do">
                    <strong>Use For:</strong>
                    <ul>
                      <li>Reusable styles</li>
                      <li>Multiple elements</li>
                      <li>Component styling</li>
                      <li>General purpose CSS</li>
                    </ul>
                  </div>
                  <div className="html-comparison-item html-dont">
                    <strong>Avoid For:</strong>
                    <ul>
                      <li>Unique elements</li>
                      <li>JavaScript targeting</li>
                      <li>Page navigation</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Best Practices Section */}
          <div 
            className="html-practices-section"
            ref={(el) => addSectionRef(el, sections.length + 2)}
          >
            <div className="html-practices-header">
              <div className="html-code-badge">
                <FaCheck className="html-badge-icon" />
                Best Practices
              </div>
              <h3 className="html-practices-title">
                ID Naming Conventions & Rules
              </h3>
            </div>
            
            <div className="html-practices-grid">
              <div className="html-practice-card html-do">
                <h4>✅ Good ID Names</h4>
                <ul>
                  <li><code>main-navigation</code></li>
                  <li><code>contact-form</code></li>
                  <li><code>page-header</code></li>
                  <li><code>user-profile</code></li>
                  <li><code>search-results</code></li>
                </ul>
              </div>
              
              <div className="html-practice-card html-dont">
                <h4>❌ Avoid These</h4>
                <ul>
                  <li><code>div1</code> (not descriptive)</li>
                  <li><code>my id</code> (contains space)</li>
                  <li><code>header</code> (too generic)</li>
                  <li><code>123element</code> (starts with number)</li>
                  <li><code>element@id</code> (special characters)</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Comprehensive Example */}
          <div 
            className="html-code-example"
            ref={(el) => addSectionRef(el, sections.length + 3)}
          >
            <div className="html-code-header">
              <div className="html-code-badge">
                <FaCode className="html-badge-icon" />
                Complete Example
              </div>
              <h3 className="html-code-title">
                Professional Website with ID Selectors
              </h3>
            </div>
            <div className="html-code-block">
              <CopyBlock
                language={language}
                text={code6}
                showLineNumbers={lineNumbers}
                theme={dracula}
                wrapLines={true}
                codeBlock
              />
            </div>
            <div className="html-code-actions">
              <a 
                href={videoUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="html-code-btn html-primary"
              >
                Watch Complete Implementation Guide
              </a>
            </div>
          </div>

          {/* Action Section */}
          <div 
            className="html-action-section"
            ref={(el) => addSectionRef(el, sections.length + 4)}
          >
            <div className="html-action-header">
              <h3>Mastered ID selectors?</h3>
              <div className="html-completion-badge">
                {getCompletionPercentage()}% of content viewed
              </div>
            </div>
            
            <div className="html-action-content">
              <p>You've learned how to use ID selectors for unique element targeting in CSS, JavaScript, and page navigation. Start implementing these in your projects!</p>
              
              <div className="html-action-buttons">
                <button 
                  className={`html-complete-button ${isCompleted ? 'html-completed' : ''}`}
                  onClick={() => setIsCompleted(true)}
                >
                  {isCompleted ? (
                    <>
                      <FaCheck className="html-button-icon" />
                      Lesson Completed
                    </>
                  ) : (
                    'Mark as Complete'
                  )}
                </button>
                
                {isCompleted && (
                  <Link to="/html-class-selectors" className="html-next-lesson-link">
                    Continue to Next Lesson
                    <FaArrowRight className="html-link-icon" />
                  </Link>
                )}
              </div>
              
              <div className="html-quick-nav">
                <span>Need more examples? </span>
                <a 
                  href={videoUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="html-nav-link"
                >
                  Watch Complete HTML Course
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </HTMLCourseNavProvider>
  );
}

export default HTMLLesson9;